import React, { useEffect } from "react"
import { graphql, navigate } from "gatsby"

import Layout from "../components/layout"
import Image from "../components/image"
import SEO from "../components/seo"

// In-memory flag: persists across client-side navigation (e.g. contact -> home)
// but resets on a full page reload, so the intro plays again on reload.
let introDismissed = false

// Home titles use a tighter line-height only when the text actually wraps.
// There is no CSS selector for "wrapped", so measure the rendered line boxes:
// a Range over the text yields one rect per line. Re-measure on resize (and
// on font load, which also resizes the element).
function HomeTitle({ children, style }) {
  const ref = React.useRef(null)
  const [wrapped, setWrapped] = React.useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => {
      const range = document.createRange()
      range.selectNodeContents(el)
      setWrapped(range.getClientRects().length > 1)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    ro.observe(el.parentElement)
    return () => ro.disconnect()
  }, [children])

  return (
    <h1
      ref={ref}
      className="home-title"
      style={{
        ...style,
        fontFamily: '"Dragon Limen", serif',
        fontWeight: "normal",
        margin: 0,
        pointerEvents: "none",
        display: "inline-block",
        textTransform: "uppercase",
        lineHeight: wrapped ? 0.78 : 0.9,
        marginBottom: wrapped ? -5 : -8,
        marginTop: wrapped ? 3 : 0
      }}
    >
      {children}
    </h1>
  )
}

const IndexPage = ({ data: { artworks } }) => {
  const [hovered, setHover] = React.useState(undefined)
  const [transit, setTransit] = React.useState(true)
  const [showIntro, setShowIntro] = React.useState(false)

  // Show the intro unless it was already dismissed during this page load.
  // It won't reappear on navigation back to the homepage, but a full reload
  // resets the flag so it plays again. (Start hidden to avoid a hydration
  // mismatch, then reveal in an effect.)
  useEffect(() => {
    if (!introDismissed) setShowIntro(true)
  }, [])

  const dismissIntro = () => {
    introDismissed = true
    setShowIntro(false)
  }

  function linkTo(slug) {
    setTransit(slug)
    setHover(undefined)
    setTimeout(() => {
      setTransit(false)
      navigate(slug)
    }, 500)
  }

  // Title fill: no hover -> solid black. While something is hovered the page
  // turns black, so the hovered title is solid white and the rest are shown
  // as a white outline (transparent fill).
  function titleStyle(id) {
    if (hovered === undefined) return { color: "black" }
    if (hovered === id) return { color: "white" }
    return { color: "transparent", WebkitTextStroke: "1px #fff" }
  }

  // Toggle the page-wide dark hover state: when an artwork is hovered the
  // background turns black and the nav + hovered title turn white.
  useEffect(() => {
    if (typeof document === "undefined") return
    document.body.classList.toggle("nav-dark", hovered !== undefined)
    return () => document.body.classList.remove("nav-dark")
  }, [hovered])

  return (
    <Layout>
      <SEO title="Home" />
      {showIntro && (
        <div
          onClick={dismissIntro}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "url(/cursor.png) 31 0, pointer",
          }}
        >
          <img
            src="/entry.gif"
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
          <span
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontFamily: '"Dragon Limen", serif',
              fontWeight: "normal",
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              fontSize: "clamp(2.5rem, 9vw, 8rem)",
              lineHeight: 1,
              color: "#fff",
              // invert against whatever frame of the gif is behind it
              mixBlendMode: "difference",
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            enter
          </span>
        </div>
      )}
      <div
        className={"row is-relative"}
        style={{ minHeight: "100%", alignItems: "center" }}
      >
        <div
          className={"col-xs-12 wrap"}
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <div
            className="col-xs-10"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            {artworks.edges.map(
              ({
                node: {
                  frontmatter,
                  id,
                  fields: { slug }
                }
              }) => (
                <div
                  key={id}
                  onClick={() => linkTo(slug)}
                  onMouseEnter={() => {
                    setHover(id)
                  }}
                  onMouseLeave={() => setHover(undefined)}
                  className="home-entry"
                  style={{ textAlign: "center" }}
                >
                  <HomeTitle
                    style={{
                      ...titleStyle(id)
                    }}
                  >
                    {frontmatter.title}
                  </HomeTitle>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}
export default IndexPage

export const pageQuery = graphql`
  query IndexPage {
    artworks: allMarkdownRemark(
      sort: { order: DESC, fields: [frontmatter___date] }
      filter: { frontmatter: { templateKey: { eq: "artwork-page" } } }
    ) {
      edges {
        node {
          id
          fields {
            slug
          }
          frontmatter {
            description
            title
            crypto {
              childImageSharp {
                fluid(maxWidth: 1028) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
            images {
              image {
                childImageSharp {
                  fluid(maxWidth: 2048) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`
